import { defineStore } from "pinia";
import { forumApi, pageApi } from "@/api";
import type {
  ForumInfo,
  ForumInfoParams,
  ApiResponse,
  PaginationInfo,
} from "@/api";
import { GAME_ID } from "./index";
import i18n from "@/i18n";
import { useLanguageStore } from "./language";
// 添加排序方式枚举
export enum OrderBy {
  DATELINE = "dateline", // 发布时间
  LASTPOST = "lastpost", // 最后回复时间
}

// 添加板块类型定义
interface ForumSection {
  id: number;
  name: string;
  fid: number;
  [key: string]: any;
}

// 标签类型定义
interface Tag {
  id: number | string;
  name: string;
  color: string;
}

const { t } = i18n.global;
// 获取推荐板块配置的函数，确保每次都能获取最新的翻译
const getRecommendSection = () => ({
  name: "recommend",
  fid: "trending",
});

export const useForumStore = defineStore("forum", {
  state: () => ({
    // 论坛信息
    forumInfo: null as ForumInfo | null,
    // 论坛列表
    forumList: [] as ForumInfo[],
    // 当前选中的板块索引
    currentSection: 0,
    // 当前板块的帖子列表
    forumThreads: [] as any[],
    // 当前的定制化模块
    customModules: [] as any[],
    // 初始加载状态
    loading: false,
    // 加载更多状态
    loadingMore: false,
    // 是否已签到
    isSigned: false,
    // 错误信息
    error: null as string | null,
    // 分页信息
    pagination: null as PaginationInfo | null,
    // 排序方式：dateline(发布时间) 或 lastpost(最后回复时间)
    orderBy: OrderBy.DATELINE as OrderBy,
    // 标签列表（从帖子列表中提取的唯一标签）
    tagInfo: [] as Tag[],
    // 当前选中的标签ID
    selectedTagId: null as number | string | null,
    // 页面帖子列表（当选中标签时使用）
    pageThreads: [] as any[],
    // 页面分页信息
    pagePagination: null as PaginationInfo | null,
  }),

  getters: {
    // 获取当前板块名称
    currentSectionName: (state) => {
      if (
        state.forumSections.length > 0 &&
        state.currentSection < state.forumSections.length
      ) {
        return state.forumSections[state.currentSection].name;
      }
      return "";
    },
    // 获取当前板块信息
    currentSectionInfo: (state) => {
      if (
        state.forumSections.length > 0 &&
        state.currentSection < state.forumSections.length
      ) {
        return state.forumSections[state.currentSection];
      }
      return null;
    },
    // 获取当前论坛信息
    currentForumInfo: (state) => state.forumInfo,
    // 获取当前论坛帖子列表
    currentForumThreads: (state) => state.forumInfo?.threads || [],
    // 获取当前论坛帖子总数
    currentForumTotal: (state) => state.forumInfo?.total || 0,
    // 是否有论坛信息
    hasForumInfo: (state) => !!state.forumInfo,
    // 获取论坛列表数量
    forumListCount: (state) => state.forumList.length,
    // 获取论坛板块
    forumSections: (state) => [
      getRecommendSection(),
      ...(state.forumInfo?.sub_forums || []),
    ],
    // 是否还有更多数据
    hasMore: (state) => {
      if (state.selectedTagId) {
        return state.pagePagination?.has_next || false;
      }
      return state.pagination?.has_next || false;
    },
    // 当前显示的帖子列表（根据是否选中标签返回不同的列表）
    currentThreads: (state) => {
      if (state.selectedTagId) {
        return state.pageThreads;
      }
      return state.forumThreads;
    },
  },

  actions: {
    /**
     * 获取指定分区的帖子分类
     */
    async getForumInfo() {
      this.loading = true;
      this.error = null;

      try {
        const response = await forumApi.getInfo(
          GAME_ID,
          useLanguageStore().targetLanguage
        );
        if (response.code === 200) {
          this.forumInfo = response.data;
          // 初始化板块列表
          if (response.data.sub_forums && response.data.sub_forums.length > 0) {
            // 获取第一个板块的帖子
            await this.getForumThreads(
              this.forumSections[this.currentSection].fid
            );
          }
        }
        return response;
      } catch (error) {
        this.error =
          error instanceof Error ? error.message : "获取论坛信息失败";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 获取指定分区的帖子数据（支持分页）
     * @param fid 板块ID
     * @param page 页码
     * @param page_size 每页数量
     * @param append 是否追加到现有列表
     * @param orderBy 排序方式：dateline(发布时间) 或 lastpost(最后回复时间)
     */
    async getForumThreads(
      fid: number,
      page: number = 1,
      page_size: number = 10,
      append: boolean = false,
      orderBy?: OrderBy
    ) {
      if (append) {
        this.loadingMore = true;
      } else {
        this.loading = true;
      }
      this.error = null;

      try {
        // 使用传入的orderBy参数，如果没有传入则使用store中的orderBy
        const currentOrderBy = orderBy || this.orderBy;
        const isRecommend = fid === getRecommendSection().fid;
        const response = await forumApi.getThreads(
          GAME_ID,
          useLanguageStore().targetLanguage,
          fid,
          page,
          page_size,
          currentOrderBy,
          isRecommend ? 1 : 0
        );
        if (response.code === 200) {
          const threads = response.data.threads || [];
          if (append) {
            // 追加模式：将新数据添加到现有列表
            this.forumThreads = [...this.forumThreads, ...threads];
          } else {
            // 非追加模式：替换现有列表
            this.forumThreads = threads;
            this.customModules = response.data.module || [];
            // 直接使用响应中的标签信息
            this.tagInfo = response.data.tag_info || [];
          }

          // 更新分页信息
          this.pagination = response.data.pagination || null;

          return response;
        } else {
          this.error = response.message || "获取帖子列表失败";
          throw new Error(this.error);
        }
      } catch (error) {
        this.error =
          error instanceof Error ? error.message : "获取帖子列表失败";
        throw error;
      } finally {
        if (append) {
          this.loadingMore = false;
        } else {
          this.loading = false;
        }
      }
    },

    /**
     * 加载更多帖子
     */
    async loadMoreThreads() {
      if (this.loading || this.loadingMore) {
        return;
      }

      // 如果选中了标签，使用 page API 加载更多
      if (this.selectedTagId) {
        if (!this.pagePagination?.has_next) {
          return;
        }
        const nextPage = (this.pagePagination.current_page || 0) + 1;
        await this.getPageThreadsByTag(this.selectedTagId, nextPage, 10, true);
      } else {
        // 否则使用 forum API 加载更多
        if (!this.pagination?.has_next) {
          return;
        }
        const nextPage =
          (this.pagination.current_page || this.pagination.page || 0) + 1;
        const currentSection = this.currentSectionInfo;

        if (currentSection) {
          await this.getForumThreads(currentSection.fid, nextPage, 10, true);
        }
      }
    },

    /**
     * 切换板块
     * @param sectionIndex 板块索引
     */
    async switchSection(sectionIndex: number) {
      if (sectionIndex === this.currentSection) return;

      this.currentSection = sectionIndex;
      // 切换板块时清除分页信息
      this.pagination = null;
      // 重置排序
      this.orderBy = OrderBy.DATELINE;
      // 清除标签选择
      this.selectedTagId = null;
      this.pageThreads = [];
      this.pagePagination = null;
      if (
        this.forumSections.length > 0 &&
        sectionIndex < this.forumSections.length
      ) {
        const section = this.forumSections[sectionIndex];
        await this.getForumThreads(section.fid);
      }
    },

    /**
     * 切换排序方式
     * @param orderBy 排序方式：dateline(发布时间) 或 lastpost(最后回复时间)
     */
    async switchOrderBy(orderBy: OrderBy) {
      if (orderBy === this.orderBy) return;

      this.orderBy = orderBy;
      // 切换排序时清除分页信息
      this.pagination = null;
      this.pagePagination = null;

      // 如果选中了标签，使用 page API
      if (this.selectedTagId) {
        this.pageThreads = [];
        await this.getPageThreadsByTag(
          this.selectedTagId,
          1,
          10,
          false,
          orderBy
        );
      } else {
        // 否则使用 forum API
        this.forumThreads = [];
        const currentSection = this.currentSectionInfo;
        if (currentSection) {
          await this.getForumThreads(currentSection.fid, 1, 10, false, orderBy);
        }
      }
    },

    /**
     * 签到
     */
    async sign() {
      const response = await forumApi.sign(GAME_ID);
      if (response.success) {
        this.isSigned = true;
        return response;
      } else {
        this.error = response.message || "签到失败";
        throw new Error(this.error);
      }
    },

    /**
     * 切换标签
     * @param tagId 标签ID，如果传入 null 则取消标签筛选
     */
    async switchTag(tagId: number | string | null) {
      if (tagId === this.selectedTagId) return;

      this.selectedTagId = tagId;
      this.pagePagination = null;

      if (tagId === null) {
        // 取消标签筛选，恢复显示论坛帖子列表
        this.pageThreads = [];
        // 重新加载当前板块的帖子
        const currentSection = this.currentSectionInfo;
        if (currentSection) {
          await this.getForumThreads(currentSection.fid, 1, 10, false);
        }
      } else {
        // 选中标签，使用 page API 获取帖子
        this.forumThreads = [];
        await this.getPageThreadsByTag(tagId, 1, 10, false);
      }
    },

    /**
     * 通过标签ID获取页面帖子列表
     * @param tagId 标签ID
     * @param page 页码
     * @param page_size 每页数量
     * @param append 是否追加到现有列表
     * @param orderBy 排序方式
     */
    async getPageThreadsByTag(
      tagId: number | string,
      page: number = 1,
      page_size: number = 10,
      append: boolean = false,
      orderBy?: OrderBy
    ) {
      if (append) {
        this.loadingMore = true;
      } else {
        this.loading = true;
      }
      this.error = null;

      try {
        const currentOrderBy = orderBy || this.orderBy;
        const response = await pageApi.getPageThread(
          Number(tagId),
          undefined,
          currentOrderBy,
          useLanguageStore().targetLanguage,
          page,
          page_size,
          GAME_ID
        );

        if (response.code === 200) {
          const threads = response.data?.threads || [];

          if (append) {
            // 追加模式：将新数据添加到现有列表
            this.pageThreads = [...this.pageThreads, ...threads];
          } else {
            // 非追加模式：替换现有列表
            this.pageThreads = threads;
          }

          // 更新分页信息
          this.pagePagination = response.data?.pagination || null;

          return response;
        }
      } catch (error) {
        this.error =
          error instanceof Error ? error.message : "获取页面帖子列表失败";
        throw error;
      } finally {
        if (append) {
          this.loadingMore = false;
        } else {
          this.loading = false;
        }
      }
    },

    /**
     * 更新指定帖子的浏览量
     * @param tid 帖子ID
     */
    updateThreadViews(tid: number) {
      // 更新 forumThreads 中的浏览量
      const threadIndex = this.forumThreads.findIndex(
        (thread) => thread.tid === tid
      );
      if (threadIndex !== -1) {
        this.forumThreads[threadIndex].views =
          (this.forumThreads[threadIndex].views || 0) + 1;
      }
      // 更新 pageThreads 中的浏览量
      const pageThreadIndex = this.pageThreads.findIndex(
        (thread) => thread.tid === tid
      );
      if (pageThreadIndex !== -1) {
        this.pageThreads[pageThreadIndex].views =
          (this.pageThreads[pageThreadIndex].views || 0) + 1;
      }
    },

    /**
     * 批量更新帖子关注状态，根据authorid更新
     */
    updateThreadFollowStatus(authorid: number, follow_action: number) {
      this.forumThreads.forEach((thread) => {
        if (thread.authorid === authorid) {
          thread.is_author_followed = follow_action;
        }
      });
      this.pageThreads.forEach((thread) => {
        if (thread.authorid === authorid) {
          thread.is_author_followed = follow_action;
        }
      });
    },
  },
});
